import CustomScrollbar from 'components/Common/CustomScrollbar';
import TextGenerate from 'components/Common/TextGenerator';
import { SelectedEventSkeleton } from 'components/Dashboard/LiveFromParliament/EventListSkeleton';
import useEventListFunctions, {
  useCallEventListItem,
} from 'components/Dashboard/LiveFromParliament/useEventListFunctions';
import { CreateAlertPopup, SearchEventPopup } from 'components/Dashboard/Navigation/NotFeaturePopups';
import checkEllipsis from 'components/Dashboard/utilities/checkEllipsis';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import timezone from 'dayjs/plugin/timezone';
import useWindowHeight from 'components/Dashboard/utilities/useWindowHeight';
import time from 'utils/time';
dayjs.extend(timezone);

function SelectedEvent({ selectedEvent }) {
  const {
    id,
    transcriberRunning,
    actualStartDateTime,
    displayStartDateTime,
    actualEndDateTime,
    subject,
    createdAtUtc,
  } = selectedEvent ?? {};

  const [eventData, setEventData] = useState({});
  const [eventBodies, setEventBodies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [scrollPosition, setScrollPosition] = useState(null);
  const [allowScrollBottom, setAllowScrollBottom] = useState(true);
  const [initialDate, setInitialDate] = useState(null);
  const [transcriptionButtonPosition, setTranscriptionButtonPosition] = useState(0);
  const [scrolling, setScrolling] = useState(false);
  const [isOpenCreateAlertPopup, setIsOpenCreateAlertPopup] = useState(false);
  const [animatedItem, setAnimatedItem] = useState(null);

  const isMobile = useIsMobile();
  const { callEvents } = useEventListFunctions();

  useCallEventListItem({
    condition: transcriberRunning && !isLoading && !!initialDate,
    eventId: id,
    data: eventBodies,
    setData: setEventBodies,
    date: initialDate,
  });

  const scrollBarRef = useRef(null);
  const callEventsFunc = useRef(null);
  const subjectElement = useRef(null);
  const goTranscriptionButton = useRef(null);
  const contentContainer = useRef(null);
  const eventBodiesRef = useRef(eventBodies);

  const conditionToShowGoToTranscriptionButton = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e ?? scrollBarRef?.current ?? {};
    return scrollTop + 20 < scrollHeight - clientHeight;
  };

  const [showGoToTranscriptionButton, setShowGoToTranscriptionButton] = useState(false);
  const [heightContainer, containerRef] = useHeightContainer();
  const { windowWidth } = useWindowHeight();
  const scrollBarHeight = isMobile ? 300 : heightContainer;

  useEffect(() => {
    if (contentContainer?.current) {
      setTranscriptionButtonPosition(
        contentContainer.current?.getBoundingClientRect()?.left +
          (contentContainer.current?.getBoundingClientRect()?.width -
            goTranscriptionButton?.current?.getBoundingClientRect()?.width) /
            2
      );
    }
  }, [contentContainer?.current, windowWidth]);

  callEventsFunc.current = async (source) => {
    try {
      setIsLoading(true);

      const dateUK = dayjs().tz('Europe/London');
      const initialDateEvent = dateUK.isSame(createdAtUtc, 'day') ? null : createdAtUtc;
      const result = await callEvents({ source, eventId: id, setInitialDate, specificDate: initialDateEvent });
      if (result?.hits) {
        const eventBodies = result.hits.map((item) => ({ ...item, text: item.body }));
        setEventBodies(eventBodies);
        setEventData(result.hits[0]);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
      setTimeout(
        () =>
          scrollBarRef?.current?.view?.scroll({
            top: scrollBarRef?.current?.view?.scrollHeight,
            behavior: 'smooth',
          }),
        10
      );
    }
  };

  useEffect(() => {
    const source = new AbortController();
    setAnimatedItem(null);
    if (selectedEvent) {
      callEventsFunc.current(source);
    }
    return () => {
      source.abort('Events cancelled by user');
    };
  }, [selectedEvent]);

  const scrollToBottom = () => {
    if (allowScrollBottom && !scrolling) {
      setTimeout(
        () =>
          scrollBarRef?.current?.view?.scroll({
            top: scrollBarRef?.current?.view?.scrollHeight,
            behavior: 'smooth',
          }),
        10
      );
    }
  };

  const hasPendingTranscription = !!animatedItem || eventBodies.some((item) => item.notShown);

  const handleFinish = () => {
    const newEventBodies = [...eventBodiesRef.current];
    const currentItemPosition = newEventBodies.findIndex((item) => item?.objectID === animatedItem?.objectID);
    if (currentItemPosition >= 0) {
      newEventBodies[currentItemPosition] = { ...newEventBodies[currentItemPosition], notShown: false };
      const notShowItems = newEventBodies?.filter((item) => item?.notShown);
      setAnimatedItem(notShowItems?.length > 0 ? notShowItems[0] : null);
    }
    setEventBodies(newEventBodies);
  };

  useEffect(() => {
    const notShownItems = eventBodies?.filter((item) => item?.notShown);
    eventBodiesRef.current = eventBodies;
    if (!animatedItem && notShownItems?.length > 0) {
      setAnimatedItem(notShownItems[0]);
    }
  }, [eventBodies]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e;
    setShowGoToTranscriptionButton(
      ((transcriberRunning && hasPendingTranscription) || (!transcriberRunning && actualEndDateTime)) &&
        conditionToShowGoToTranscriptionButton(e)
    );
    setScrollPosition(e.scrollTop);
    const scrolledToBottom = scrollTop === scrollHeight - clientHeight;
    setAllowScrollBottom(scrolledToBottom);
  };

  const topFadeCondition = scrollPosition > 20 && actualStartDateTime;
  const bottomFadeCondition =
    scrollPosition + scrollBarHeight < scrollBarRef?.current?.view?.scrollHeight - 50 && actualStartDateTime;

  const conditionOfContent = eventBodies?.length > 0;

  const urlWithoutSearch = () => {
    if (!!eventData?.sourceUrl) {
      const cleanUrl = new URL(eventData?.sourceUrl);
      cleanUrl.search = '';
      return cleanUrl.href;
    }
    return null;
  };

  return (
    <>
      {isLoading || !selectedEvent ? (
        <SelectedEventSkeleton />
      ) : (
        <div className='rounded bg-white border specific-top-padding-30 px-3 px-lg-5' ref={contentContainer}>
          {conditionOfContent && (
            <>
              {transcriberRunning && (
                <p>
                  <span className='tag-keyword-list-live'>
                    <span className='element-record'></span>
                  </span>
                  <span className='light-60-text'>Transcribing...</span>
                </p>
              )}
              {actualEndDateTime && (
                <p className='element-record-past-event'>
                  <span className='icon-record mr-2 color-main-yellow'></span>
                  <span className='light-60-text'>This event was transcribed in real time</span>
                </p>
              )}
            </>
          )}
          <div>
            {conditionOfContent && (
              <>
                <h3 className='title-h5 font-weight-bold'>{eventData?.title}</h3>
                <p className={`heading-date mb-0 pt-2 d-flex align-items-center`}>
                  {subject && (
                    <>
                      <span
                        ref={subjectElement}
                        className={`paragraph-p3 parliament-live-item-subject`}
                        data-tooltip-content={checkEllipsis(subjectElement?.current) ? subject : ''}
                        data-tooltip-id={'general-tooltip'}
                      >
                        {subject}
                      </span>
                      <span className='dot mt-0'></span>
                    </>
                  )}
                  <span className='paragraph-p3 nowrap-item'>
                    {`${actualStartDateTime ? 'Started' : 'Starts'} at ${time(
                      actualStartDateTime ?? displayStartDateTime
                    )
                      .formatTime()
                      .replaceAll(' ', '')
                      .toLowerCase()}`}
                  </span>
                </p>
                <div className='d-flex align-items-center mt-4 mb-3 flex-wrap flex-lg-nowrap gap-15'>
                  <a
                    href={urlWithoutSearch()}
                    target='_blank'
                    rel={'noopener noreferrer'}
                    className='specific-x-padding-24 py-1 rounded-pill border bg-main-white general-button hovered-button text-light-blue link-hover reset-link nowrap-item'
                  >
                    <span className='icon-single-play d-inline-block-centered mr-2' />
                    {actualStartDateTime && <>{transcriberRunning ? 'Watch this event live' : `Watch this event`}</>}
                  </a>
                  <SearchInput />
                </div>
              </>
            )}
            <div ref={containerRef}></div>
            <CustomScrollbar
              onScroll={(e) => {
                handleScroll(e);
              }}
              onScrollStart={() => {
                setScrolling(true);
              }}
              onScrollStop={() => {
                setScrolling(false);
              }}
              className={`main-content-scrollbar ${topFadeCondition ? 'scrollbar-top-fade' : ''} ${
                bottomFadeCondition ? 'scrollbar-bottom-fade' : ''
              } `}
              style={{ height: `${scrollBarHeight}px` }}
              maximalThumbYSize={100}
              ref={scrollBarRef}
              contentClass={'ScrollbarsCustom-Content overscroll-behavior-contain'}
            >
              <div className='selected-event-bodies-container'>
                {conditionOfContent ? (
                  <>
                    {eventBodies
                      .filter((item) => !item.notShown)
                      .map((item, i, arr) => {
                        const url = new URL(`${window.location?.origin}${item?.contentUrl}`);
                        const searchParams = new URLSearchParams(url?.search);
                        const timeParam = searchParams?.get('in');
                        const currentTime = dayjs(timeParam, 'HH:mm:ss');
                        const currentMinute = currentTime.format('HH:mm');
                        let showTime = true;

                        if (i > 0) {
                          const prevItem = arr[i - 1];
                          const prevUrl = new URL(`${window.location?.origin}${prevItem?.contentUrl}`);
                          const prevParams = new URLSearchParams(prevUrl?.search);
                          const prevTimeParam = prevParams?.get('in');
                          const prevTime = dayjs(prevTimeParam, 'HH:mm:ss');
                          const prevMinute = prevTime.format('HH:mm');

                          showTime = currentMinute !== prevMinute;
                        }
                        return (
                          <>
                            {showTime && (
                              <p className={`specific-top-margin-6 mb-1 flex-centered main-light-text paragraph-p5`}>
                                <span className='icon-clock mr-1' />
                                <span>{time(item?.dateTime)?.formatTime()}</span>
                              </p>
                            )}
                            <p key={`event-text-${i}`} className='paragraph-p1 line-height-1-5 mb-1'>
                              {item.text}
                            </p>
                          </>
                        );
                      })}

                    {!!animatedItem && (
                      <p className='paragraph-p1 line-height-1-5 mb-5'>
                        <TextGenerate
                          key={animatedItem?.objectID}
                          onFinish={handleFinish}
                          onCharacterAdd={(i) => {
                            if (i % 10 === 0) scrollToBottom();
                          }}
                        >
                          {animatedItem?.text}
                        </TextGenerate>
                      </p>
                    )}
                  </>
                ) : (
                  <p className='paragraph-p1 line-height-1-5 mb-5 specific-top-margin-80 text-center light-65-text'>
                    When this event starts, the live transcription will appear here. To be alerted in real time if your
                    organisation or key interests are mentioned,{' '}
                    <button
                      className='general-button text-underline-hover blue-text cursor-pointer'
                      onClick={() => setIsOpenCreateAlertPopup(true)}
                    >
                      create a keyword alert
                    </button>
                  </p>
                )}
                <button
                  ref={goTranscriptionButton}
                  className='rounded-pill bg-main-blue px-4 py-2 general-button position-fixed text-white z-max go-to-live-transcription'
                  style={{
                    visibility: showGoToTranscriptionButton ? `visible` : 'hidden',
                    opacity: showGoToTranscriptionButton ? 1 : 0,
                    bottom: '20px',
                    left: `${transcriptionButtonPosition}px`,
                  }}
                  onClick={() => {
                    setShowGoToTranscriptionButton(false);
                    scrollBarRef?.current?.view?.scroll({
                      top: scrollBarRef?.current?.view?.scrollHeight,
                      behavior: 'smooth',
                    });
                    setAllowScrollBottom(true);
                  }}
                >
                  <span className='dropdown-item-element dropdown-item-element-11'>
                    {transcriberRunning ? 'Go to live transcription' : 'Go to end of transcription'}
                  </span>
                </button>
              </div>
            </CustomScrollbar>
          </div>
        </div>
      )}
      <CreateAlertPopup isOpen={isOpenCreateAlertPopup} setIsOpen={setIsOpenCreateAlertPopup} />
    </>
  );
}

const SearchInput = () => {
  const [searchValue, setSearchValue] = useState('');
  const [openSearchPopup, setOpenSearchPopup] = useState(false);
  const inputSearch = useRef(null);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
        event.preventDefault();
        setOpenSearchPopup(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <>
      <div className='rounded-pill border flex-centered specific-right-padding-7 specific-left-padding-15 specific-y-padding-6 flex-grow-1 bg-white specific-right-margin-10 search-events-input'>
        <div className='flex-grow-1 d-flex align-items-center'>
          <span className='icon-search mr-2'></span>
          <input
            className='input-empty w-100 paragraph-p1 pr-2'
            placeholder='Search within this transcript'
            value={searchValue}
            ref={inputSearch}
            onChange={(e) => {
              setSearchValue(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.keyCode === 13) {
                inputSearch.current.blur();
                setOpenSearchPopup(true);
              }
            }}
          />
        </div>
        <div
          className='close-icon-container'
          style={{
            visibility: searchValue !== '' ? 'visible' : 'hidden',
          }}
        >
          <button
            className='general-button close-icon'
            data-tooltip-content='Clear search'
            data-tooltip-id='general-tooltip'
            data-tooltip-place='right'
            onClick={(e) => {
              e.stopPropagation();
              setSearchValue('');
            }}
          />
        </div>
      </div>
      <SearchEventPopup
        isOpen={openSearchPopup}
        setIsOpen={setOpenSearchPopup}
        onAfterClose={() => {
          setSearchValue('');
        }}
      />
    </>
  );
};
export default SelectedEvent;
